import type { ReactNode } from "react";

import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import type { Topic } from "../../../api-client/demandSearchTrendsTopicsAPI";

import { TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItemTooltip as GroupItemTooltip } from "./TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItemTooltip";
import type { FlattenedTopic } from "./types";

const TopicItemsContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
`;

const Divider = styled.div`
    flex-shrink: 0;
    margin-left: 8px;
    margin-right: 8px;
    margin-bottom: 8px;
    border: none;
    height: 1px;
    background-color: ${colorsPalettes.carbon[50]};
`;

type TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItemsProps = {
    parentTopic: Topic;
    items: FlattenedTopic[];
    placement: "left" | "right";
    onItemClick: (topicId: string) => void;
};

export const TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItems = ({
    items,
    parentTopic,
    placement,
    onItemClick,
}: TopicsDiscoverAllTopicsModalMainSectionTopicsTopicGroupItemsProps) => {
    let currentParentId: string | null = null;
    const topicItems: ReactNode[] = [];

    for (let i = 0; i < items.length; i++) {
        const { item, parentId } = items[i];

        if (parentId !== null && parentId !== currentParentId) {
            topicItems.push(<Divider key={`divider-${parentId}`} />);
            currentParentId = parentId;
        }

        if (parentId === null) {
            currentParentId = null;
        }

        if (item.Level === parentTopic.Level + 1) {
            topicItems.push(
                <GroupItemTooltip
                    item={item}
                    key={item.Id}
                    placement={placement}
                >
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon[500]}
                        padding="10px 16px"
                        borderRadius="6px"
                        cursor="pointer"
                        whenHover={{
                            background: rgba(colorsPalettes.blue[400], 0.1),
                            color: colorsPalettes.blue[500],
                        }}
                        onClick={() => onItemClick(item.Id)}
                    >
                        {item.Name}
                    </Text>
                </GroupItemTooltip>,
            );
        } else {
            topicItems.push(
                <GroupItemTooltip
                    item={item}
                    key={item.Id}
                    placement={placement}
                >
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon[500]}
                        margin="0 4px"
                        padding="8px 16px 8px 28px"
                        borderRadius="6px"
                        cursor="pointer"
                        whenHover={{
                            background: rgba(colorsPalettes.blue[400], 0.1),
                            color: colorsPalettes.blue[500],
                        }}
                        onClick={() => onItemClick(item.Id)}
                    >
                        {item.Name}
                    </Text>
                </GroupItemTooltip>,
            );
        }
    }

    return <TopicItemsContainer>{topicItems}</TopicItemsContainer>;
};
